import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Printer, Send, Truck, Package, MapPin, Calendar, Filter, Search, X } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import api from '../services/api';

const GRNCreation = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  // State management
  const [showForm, setShowForm] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [formType, setFormType] = useState('103'); // '103' or '105'
  const [showBulkGRNModal, setShowBulkGRNModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [bulkGRNData, setBulkGRNData] = useState({
    storage_location: '',
    procurement_type: 'NON_JIT'
  });
  const [formData, setFormData] = useState({
    // Common fields
    materialId: '',
    receivedQuantity: '',
    storageLocation: '',
    unitPrice: '',
    remarks: '',

    // Form 103 fields (PO-based)
    truckArrivalId: '',
    purchaseOrderId: '',
    procurementType: 'NON_JIT',

    // Form 105 fields (non-PO)
    supplierName: '',
    receiptType: '',
    referenceDocument: ''
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch GRNs
  const { data: grnsData = [], isLoading: grnsLoading } = useQuery({
    queryKey: ['grns', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/qc/grn/?${params.toString()}`);
      return response.data.results || response.data;
    }
  });

  // Fetch trucks ready for GRN (in GRN_CREATION workflow state)
  const { data: grnQueueData, isLoading: grnQueueLoading } = useQuery({
    queryKey: ['grn-queue'],
    queryFn: async () => {
      const response = await api.get('/warehouse/material-receipts/grn_queue/');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const trucksReadyForGRN = grnQueueData?.trucks || [];

  // Fetch materials from material master
  const { data: materials = [] } = useQuery({
    queryKey: ['materials'],
    queryFn: async () => {
      const response = await api.get('/management/materials/');
      return response.data.results || response.data;
    }
  });

  // Fetch purchase orders
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const response = await api.get('/procurement/purchase-orders/?status=OPEN');
      return response.data.results || response.data;
    }
  });

  // Storage locations (hardcoded for now - TODO: create backend endpoint)
  const storageLocations = [
    { value: 'WH-A-R01', label: 'Warehouse A - Rack 1' },
    { value: 'WH-A-R02', label: 'Warehouse A - Rack 2' },
    { value: 'WH-A-R03', label: 'Warehouse A - Rack 3' },
    { value: 'WH-B-R01', label: 'Warehouse B - Rack 1' },
    { value: 'WH-B-R02', label: 'Warehouse B - Rack 2' },
    { value: 'WH-C-COLD', label: 'Warehouse C - Cold Storage' },
    { value: 'WH-D-HAZ', label: 'Warehouse D - Hazardous Materials' },
  ];

  // Update GRNs when data changes

  // Create GRN mutation
  const createGRNMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/qc/grn/create_grn/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['grns']);
      setShowForm(false);
      resetForm();
      success(`GRN ${data.grn_number} created successfully!`);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to create GRN');
    }
  });

  // Bulk GRN creation mutation
  const createBulkGRNMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/warehouse/material-receipts/create_bulk_grn/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['grns']);
      queryClient.invalidateQueries(['grn-queue']);
      setShowBulkGRNModal(false);
      setSelectedTruck(null);
      setBulkGRNData({ storage_location: '', procurement_type: 'NON_JIT' });
      success(`${data.message} - Total value: ₹${data.grn.total_value.toLocaleString('en-IN')}`);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to create GRN');
    }
  });

  // Post to SAP mutation
  const postToSAPMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/qc/grn/${id}/post_to_sap/`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['grns']);
      success('GRN posted to SAP successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to post GRN to SAP');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const grnData = {
      form_type: formType,
      material_id: formData.materialId,
      received_quantity: formData.receivedQuantity,
      storage_location: formData.storageLocation,
      unit_price: formData.unitPrice || null,
      remarks: formData.remarks
    };

    // Add Form 103 specific fields
    if (formType === '103') {
      grnData.truck_arrival_id = formData.truckArrivalId;
      grnData.purchase_order_id = formData.purchaseOrderId || null;
      grnData.procurement_type = formData.procurementType;
    }

    // Add Form 105 specific fields
    if (formType === '105') {
      grnData.supplier_name = formData.supplierName;
      grnData.receipt_type = formData.receiptType;
      grnData.reference_document = formData.referenceDocument;
    }

    createGRNMutation.mutate(grnData);
  };

  const handlePostToSAP = (id) => {
    postToSAPMutation.mutate(id);
  };

  const resetForm = () => {
    setFormData({
      // Common fields
      materialId: '',
      receivedQuantity: '',
      storageLocation: '',
      unitPrice: '',
      remarks: '',

      // Form 103 fields
      truckArrivalId: '',
      purchaseOrderId: '',
      procurementType: 'NON_JIT',

      // Form 105 fields
      supplierName: '',
      receiptType: '',
      referenceDocument: ''
    });
    setFormType('103');
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Handle creating GRN from queue
  const handleCreateGRNFromQueue = (truck) => {
    setSelectedTruck(truck);
    setBulkGRNData({
      storage_location: '',
      procurement_type: 'NON_JIT'
    });
    setShowBulkGRNModal(true);
  };

  // Handle bulk GRN submission
  const handleBulkGRNSubmit = (e) => {
    e.preventDefault();

    if (!bulkGRNData.storage_location) {
      showError('Please select a storage location');
      return;
    }

    createBulkGRNMutation.mutate({
      truck_id: selectedTruck.truck_id,
      storage_location: bulkGRNData.storage_location,
      procurement_type: bulkGRNData.procurement_type
    });
  };

  // Auto-calculate rejected quantity
  useEffect(() => {
    const receivedQty = parseFloat(formData.receivedQty) || 0;
    const acceptedQty = parseFloat(formData.acceptedQty) || 0;
    const calculatedRejectedQty = receivedQty - acceptedQty;

    if (calculatedRejectedQty >= 0 && formData.rejectedQty !== calculatedRejectedQty.toString()) {
      setFormData(prev => ({
        ...prev,
        rejectedQty: calculatedRejectedQty.toString()
      }));
    }
  }, [formData.receivedQty, formData.acceptedQty]);

  const stats = [
    { label: 'Total GRNs', value: grnsData.length, color: 'bg-blue-500' },
    { label: 'Draft', value: grnsData.filter(g => g.status === 'DRAFT').length, color: 'bg-yellow-500' },
    { label: 'Posted to SAP', value: grnsData.filter(g => g.status === 'POSTED').length, color: 'bg-green-500' },
  ];

  // Prepare filter options
  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'PENDING_SAP', label: 'Pending SAP' },
        { value: 'POSTED', label: 'Posted' },
        { value: 'FAILED', label: 'Failed' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">GRN Creation</h1>
          <p className="text-gray-600 mt-2">Goods Receipt Note creation and SAP integration</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary"
        >
          + Create New GRN
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} text-white p-3 rounded-lg`}>
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* GRN Queue - Trucks Ready for GRN */}
      {grnQueueLoading ? (
        <div className="card">
          <LoadingState message="Loading GRN queue..." />
        </div>
      ) : trucksReadyForGRN.length > 0 && (
        <div className="card border-2 border-blue-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Truck className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Ready for GRN Creation</h2>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                {trucksReadyForGRN.length} truck{trucksReadyForGRN.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {trucksReadyForGRN.map((truck) => (
              <div
                key={truck.truck_id}
                className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                  truck.is_breached ? 'border-red-500 bg-red-50' :
                  truck.is_at_risk ? 'border-yellow-500 bg-yellow-50' :
                  'border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Truck Header */}
                    <div className="flex items-center space-x-3 mb-3">
                      <Truck className={`w-5 h-5 ${
                        truck.is_breached ? 'text-red-600' :
                        truck.is_at_risk ? 'text-yellow-600' :
                        'text-blue-600'
                      }`} />
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">{truck.truck_number}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>{truck.supplier_name}</span>
                          <span>•</span>
                          <span>PO: {truck.po_number}</span>
                          <span>•</span>
                          <span className={
                            truck.is_breached ? 'text-red-600 font-semibold' :
                            truck.is_at_risk ? 'text-yellow-600 font-semibold' :
                            'text-gray-600'
                          }>
                            Waiting: {truck.time_in_grn_minutes} min
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Materials List */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Package className="w-4 h-4 mr-1" />
                        Materials ({truck.materials.length})
                      </h4>
                      <div className="space-y-2">
                        {truck.materials.map((material, idx) => (
                          <div
                            key={material.receipt_id}
                            className="flex items-center justify-between bg-white p-2 rounded border border-gray-200"
                          >
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">
                                {material.material_code} - {material.material_description}
                              </div>
                              <div className="text-xs text-gray-600 mt-1 space-x-3">
                                <span>Batch: {material.batch_number || 'N/A'}</span>
                                <span>•</span>
                                <span className="text-green-600 font-medium">
                                  Accepted: {material.accepted_quantity} {material.unit_of_measure}
                                </span>
                                {material.rejected_quantity > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-red-600">
                                      Rejected: {material.rejected_quantity} {material.unit_of_measure}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-sm font-semibold text-gray-900">
                                ₹{(material.accepted_quantity * material.unit_price).toLocaleString('en-IN', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </div>
                              <div className="text-xs text-gray-500">
                                @₹{material.unit_price}/{material.unit_of_measure}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Total Value */}
                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-lg">
                      <span className="text-sm font-semibold text-gray-700">Total GRN Value</span>
                      <span className="text-lg font-bold text-blue-700">
                        ₹{truck.materials.reduce((sum, m) => sum + (m.accepted_quantity * m.unit_price), 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="ml-4">
                    <button
                      onClick={() => handleCreateGRNFromQueue(truck)}
                      className="btn-primary whitespace-nowrap"
                    >
                      Create GRN
                    </button>
                  </div>
                </div>

                {/* SLA Warning */}
                {truck.is_at_risk && !truck.is_breached && (
                  <div className="mt-3 bg-yellow-100 border border-yellow-300 rounded px-3 py-2 text-sm text-yellow-800">
                    ⚠️ SLA at risk - Please create GRN soon
                  </div>
                )}
                {truck.is_breached && (
                  <div className="mt-3 bg-red-100 border border-red-300 rounded px-3 py-2 text-sm text-red-800">
                    🚨 SLA breached - Immediate action required
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchValue={filters.search}
        searchPlaceholder="Search by GRN number, truck, material..."
      />

      {/* GRN List */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">GRN Records</h2>

        {grnsLoading ? (
          <LoadingState message="Loading GRNs..." />
        ) : grnsData.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No GRNs found"
            message={filters.search || filters.status !== 'all'
              ? "Try adjusting your filters"
              : "Click 'Create New GRN' to get started"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">GRN Number</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Truck</th>
                  <th className="text-left p-3">Form Type</th>
                  <th className="text-left p-3">Quantity</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {grnsData.map((grn) => (
                  <tr
                    key={grn.id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedGrn(grn)}
                  >
                    <td className="p-3 font-semibold text-nexgensis-primary">{grn.grn_number || `GRN-${grn.id}`}</td>
                    <td className="p-3">{grn.grn_date || new Date().toISOString().split('T')[0]}</td>
                    <td className="p-3">{grn.truck_number || 'N/A'}</td>
                    <td className="p-3">{grn.form_type === '103' ? `Form 103 (${grn.procurement_type_display || grn.procurement_type || 'N/A'})` : `Form 105 (${grn.receipt_type_display || grn.receipt_type || 'N/A'})`}</td>
                    <td className="p-3">{grn.total_quantity || grn.quantity}</td>
                    <td className="p-3">
                      <span className={`px-3 py-1 rounded text-sm font-medium ${
                        grn.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                        grn.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                        grn.status === 'PENDING_SAP' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {grn.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="text-blue-600 hover:text-blue-800"
                          title="Print GRN"
                          onClick={() => setSelectedGrn(grn)}
                        >
                          <Printer className="w-5 h-5" />
                        </button>
                        {(grn.status === 'DRAFT' || grn.status === 'FAILED') && (
                          <button
                            onClick={() => handlePostToSAP(grn.id)}
                            className="text-green-600 hover:text-green-800"
                            title="Post to SAP"
                            disabled={postToSAPMutation.isLoading}
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create GRN Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-6">Create Goods Receipt Note</h2>

            {/* Tab Navigation */}
            <div className="flex border-b mb-6">
              <button
                type="button"
                onClick={() => setFormType('103')}
                className={`px-6 py-3 font-medium transition-colors ${
                  formType === '103'
                    ? 'border-b-2 border-nexgensis-primary text-nexgensis-primary'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Form 103 - With PO
              </button>
              <button
                type="button"
                onClick={() => setFormType('105')}
                className={`px-6 py-3 font-medium transition-colors ${
                  formType === '105'
                    ? 'border-b-2 border-nexgensis-primary text-nexgensis-primary'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Form 105 - Without PO
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Form 103 - PO-based GRN */}
              {formType === '103' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Form 103:</strong> Goods receipt WITH purchase order. Requires truck arrival and optional PO reference.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Truck Number - Required for Form 103 */}
                    <SmartDropdown
                      label="Truck Number"
                      name="truckArrivalId"
                      value={formData.truckArrivalId}
                      onChange={(e) => setFormData({...formData, truckArrivalId: e.target.value})}
                      options={trucksReadyForGRN.map(truck => ({
                        value: truck.truck_id,
                        label: `${truck.truck_number} - ${truck.supplier_name}`
                      }))}
                      icon={Truck}
                      required
                      placeholder="Select truck from queue"
                    />

                    {/* PO Number - Optional for Form 103 */}
                    <SmartDropdown
                      label="PO Number (Optional)"
                      name="purchaseOrderId"
                      value={formData.purchaseOrderId}
                      onChange={(e) => setFormData({...formData, purchaseOrderId: e.target.value})}
                      options={purchaseOrders.map(po => ({
                        value: po.id,
                        label: `${po.po_number} - ${po.supplier?.name || ''}`
                      }))}
                      icon={FileText}
                      placeholder="Select PO (optional)"
                    />

                    {/* Procurement Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Procurement Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.procurementType}
                        onChange={(e) => setFormData({...formData, procurementType: e.target.value})}
                        className="input-field"
                        required
                      >
                        <option value="NON_JIT">Non-JIT</option>
                        <option value="JIT">Just-In-Time (JIT)</option>
                        <option value="IMPORT">Import</option>
                        <option value="FOC">Free of Cost (FoC)</option>
                      </select>
                      {formData.procurementType === 'FOC' && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ FoC materials require 100% QC inspection</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Form 105 - Non-PO GRN */}
              {formType === '105' && (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-purple-800">
                      <strong>Form 105:</strong> Goods receipt WITHOUT purchase order. For returns, transfers, samples, and other non-PO receipts. No truck required.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Supplier Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supplier Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.supplierName}
                        onChange={(e) => setFormData({...formData, supplierName: e.target.value})}
                        className="input-field"
                        placeholder="ABC Chemicals Pvt Ltd"
                        required
                      />
                    </div>

                    {/* Receipt Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Receipt Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.receiptType}
                        onChange={(e) => setFormData({...formData, receiptType: e.target.value})}
                        className="input-field"
                        required
                      >
                        <option value="">Select type...</option>
                        <option value="FREE_GOODS">Free Goods</option>
                        <option value="RETURN_FROM_PRODUCTION">Return from Production</option>
                        <option value="SAMPLE">Sample Material</option>
                        <option value="TRANSFER">Transfer Posting</option>
                        <option value="INITIAL_STOCK">Initial Stock</option>
                        <option value="OTHER">Other Receipt</option>
                      </select>
                      {(formData.receiptType === 'FREE_GOODS' || formData.receiptType === 'SAMPLE') && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ Samples/Free goods require 100% QC inspection</p>
                      )}
                    </div>

                    {/* Reference Document */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reference Document
                      </label>
                      <input
                        type="text"
                        value={formData.referenceDocument}
                        onChange={(e) => setFormData({...formData, referenceDocument: e.target.value})}
                        className="input-field"
                        placeholder="SAMPLE-REF-2025-012 or PROD-RETURN-2025-045"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Common Fields for Both Forms */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 mb-4">Material & Quantity Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Material Dropdown */}
                  <SmartDropdown
                    label="Material"
                    name="materialId"
                    value={formData.materialId}
                    onChange={(e) => setFormData({...formData, materialId: e.target.value})}
                    options={materials.map(material => ({
                      value: material.id,
                      label: `${material.code} - ${material.description}`
                    }))}
                    icon={Package}
                    required
                    placeholder="Select material"
                  />

                  {/* Received Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Received Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.receivedQuantity}
                      onChange={(e) => setFormData({...formData, receivedQuantity: e.target.value})}
                      className="input-field"
                      placeholder="1000.000"
                      required
                    />
                  </div>

                  {/* Storage Location */}
                  <SmartDropdown
                    label="Storage Location"
                    name="storageLocation"
                    value={formData.storageLocation}
                    onChange={(e) => setFormData({...formData, storageLocation: e.target.value})}
                    options={storageLocations}
                    icon={MapPin}
                    required
                    placeholder="Select storage location"
                  />

                  {/* Unit Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit Price (Optional)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.unitPrice}
                      onChange={(e) => setFormData({...formData, unitPrice: e.target.value})}
                      className="input-field"
                      placeholder="85.50"
                    />
                  </div>

                  {/* Remarks */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                    <textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                      className="input-field"
                      rows="3"
                      placeholder="Additional notes or remarks..."
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                  disabled={createGRNMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createGRNMutation.isLoading}
                >
                  {createGRNMutation.isLoading ? 'Creating...' : 'Create GRN'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GRN Detail Modal */}
      {selectedGrn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedGrn.grn_number}</h2>
                <p className="text-gray-600 mt-1">Goods Receipt Note Details</p>
              </div>
              <button
                onClick={() => setSelectedGrn(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-4 py-2 rounded text-sm font-medium ${
                  selectedGrn.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                  selectedGrn.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                  selectedGrn.status === 'PENDING_SAP' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedGrn.status}
                </span>
                <span className="text-sm text-gray-600">
                  {new Date(selectedGrn.grn_date).toLocaleDateString()}
                </span>
              </div>

              {/* Form Type and Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Form Type</label>
                    <p className="text-lg font-semibold mt-1">{selectedGrn.form_type_display || `Form ${selectedGrn.form_type}`}</p>
                  </div>

                  {selectedGrn.form_type === '103' ? (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Procurement Type</label>
                        <p className="text-lg mt-1">{selectedGrn.procurement_type_display || selectedGrn.procurement_type || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">PO Number</label>
                        <p className="text-lg mt-1">{selectedGrn.po_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Truck Number</label>
                        <p className="text-lg mt-1">{selectedGrn.truck_number || 'N/A'}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Receipt Type</label>
                        <p className="text-lg mt-1">{selectedGrn.receipt_type_display || selectedGrn.receipt_type || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Supplier Name</label>
                        <p className="text-lg mt-1">{selectedGrn.supplier_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Reference Document</label>
                        <p className="text-lg mt-1">{selectedGrn.reference_document || 'N/A'}</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">QC Inspection Level</label>
                    <p className={`text-lg mt-1 font-semibold ${
                      selectedGrn.qc_inspection_level === '100_PERCENT' ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {selectedGrn.qc_inspection_level_display || selectedGrn.qc_inspection_level || 'N/A'}
                      {selectedGrn.qc_inspection_level === '100_PERCENT' && ' ⚠️'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Quantity</label>
                    <p className="text-lg mt-1">{selectedGrn.total_quantity || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Total Value</label>
                    <p className="text-lg mt-1">₹{parseFloat(selectedGrn.total_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>

              {/* SAP Details (if posted) */}
              {selectedGrn.status === 'POSTED' && selectedGrn.sap_material_document && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">SAP Integration Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Material Document:</span>
                      <span className="ml-2 font-medium">{selectedGrn.sap_material_document}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Document Year:</span>
                      <span className="ml-2 font-medium">{selectedGrn.sap_document_year}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Posted Date:</span>
                      <span className="ml-2 font-medium">{new Date(selectedGrn.posted_date).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Details (if failed) */}
              {selectedGrn.status === 'FAILED' && selectedGrn.sap_posting_error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-2">SAP Posting Error</h3>
                  <p className="text-sm text-red-700">{selectedGrn.sap_posting_error}</p>
                </div>
              )}

              {/* Line Items Table */}
              {selectedGrn.line_items && selectedGrn.line_items.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Line Items ({selectedGrn.line_items.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Storage Location</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO Line</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedGrn.line_items.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.line_number}</td>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">{item.material_code}</div>
                              <div className="text-gray-500 text-xs">{item.material_description}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {parseFloat(item.quantity).toFixed(3)} {item.unit_of_measure}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              ₹{parseFloat(item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              ₹{parseFloat(item.line_value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.storage_location}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {item.batch_number || '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {item.po_line_item || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="4" className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Total:</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                            ₹{parseFloat(selectedGrn.total_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td colSpan="3"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* SAP Integration Details */}
              {selectedGrn.sap_plant && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">SAP Integration Fields</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Plant:</span>
                      <span className="ml-2 font-medium">{selectedGrn.sap_plant}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Movement Type:</span>
                      <span className="ml-2 font-medium">{selectedGrn.sap_movement_type}</span>
                    </div>
                    {selectedGrn.delivery_note && (
                      <div>
                        <span className="text-gray-600">Delivery Note:</span>
                        <span className="ml-2 font-medium">{selectedGrn.delivery_note}</span>
                      </div>
                    )}
                    {selectedGrn.bill_of_lading && (
                      <div>
                        <span className="text-gray-600">Bill of Lading:</span>
                        <span className="ml-2 font-medium">{selectedGrn.bill_of_lading}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Traceability Info */}
              {selectedGrn.form_type === '103' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Traceability Chain</h3>
                  <p className="text-sm text-blue-700">
                    PO → Supplier → Truck → Invoice → E-Way Bill → Material → GRN → QC Inspection
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setSelectedGrn(null)}
                  className="btn-secondary"
                >
                  Close
                </button>
                <button
                  className="btn-primary"
                  title="Print GRN"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print GRN
                </button>
                {(selectedGrn.status === 'DRAFT' || selectedGrn.status === 'FAILED') && (
                  <button
                    onClick={() => {
                      handlePostToSAP(selectedGrn.id);
                      setSelectedGrn(null);
                    }}
                    className="btn-primary bg-green-600 hover:bg-green-700"
                    disabled={postToSAPMutation.isLoading}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Post to SAP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk GRN Creation Modal */}
      {showBulkGRNModal && selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-6">Create GRN for {selectedTruck.truck_number}</h2>

            <form onSubmit={handleBulkGRNSubmit} className="space-y-6">
              {/* Truck and PO Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Supplier:</span>
                    <span className="ml-2 font-semibold">{selectedTruck.supplier_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">PO Number:</span>
                    <span className="ml-2 font-semibold">{selectedTruck.po_number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Materials:</span>
                    <span className="ml-2 font-semibold">{selectedTruck.materials.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Value:</span>
                    <span className="ml-2 font-semibold text-green-600">
                      ₹{selectedTruck.materials.reduce((sum, m) => sum + (m.accepted_quantity * m.unit_price), 0).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Materials Summary */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Materials to Include in GRN</h3>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {selectedTruck.materials.map((material, idx) => (
                    <div key={idx} className="border border-gray-200 rounded p-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-sm">
                            {material.material_code} - {material.material_description}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Batch: {material.batch_number || 'N/A'} •
                            Accepted: {material.accepted_quantity} {material.unit_of_measure}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            ₹{(material.accepted_quantity * material.unit_price).toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GRN Configuration */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">GRN Configuration</h3>

                {/* Storage Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Storage Location <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={bulkGRNData.storage_location}
                    onChange={(e) => setBulkGRNData({...bulkGRNData, storage_location: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="">Select storage location...</option>
                    {storageLocations.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    This storage location will be used for all materials in this GRN
                  </p>
                </div>

                {/* Procurement Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Procurement Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={bulkGRNData.procurement_type}
                    onChange={(e) => setBulkGRNData({...bulkGRNData, procurement_type: e.target.value})}
                    className="input-field"
                    required
                  >
                    <option value="NON_JIT">Non-JIT</option>
                    <option value="JIT">Just-In-Time (JIT)</option>
                    <option value="IMPORT">Import</option>
                    <option value="FOC">Free of Cost (FoC)</option>
                  </select>
                  {bulkGRNData.procurement_type === 'FOC' && (
                    <p className="text-xs text-orange-600 mt-1">⚠️ FoC materials will require 100% QC inspection</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkGRNModal(false);
                    setSelectedTruck(null);
                    setBulkGRNData({ storage_location: '', procurement_type: 'NON_JIT' });
                  }}
                  className="btn-secondary"
                  disabled={createBulkGRNMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createBulkGRNMutation.isLoading}
                >
                  {createBulkGRNMutation.isLoading ? 'Creating GRN...' : 'Create GRN'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GRNCreation;
